/**
 * The presentation model behind the skill tree panel.
 *
 * ## This module decides nothing about the game
 *
 * Whether a skill *may* be raised is a rule, and rules live in
 * `packages/core`. They arrive here already resolved, on `allocatable` and
 * `lockedReason`, and this module never re-derives them: it does not read
 * `requiredLevel` against a character level and it does not walk a
 * prerequisite chain to decide anything. A level gate written here as well as
 * in core would be the same rule in two places, and the second copy is the one
 * that goes stale — which is the exact failure `packages/core` exists to
 * prevent.
 *
 * What it does own is presentation: where a skill sits on the grid, which
 * lines are drawn between which cells, what a screen reader is told, and the
 * two cases below where the panel declines to offer a control at all.
 *
 * ## The panel does not ask for what will be refused
 *
 * Two blocks are applied on top of the caller's decision:
 *
 *   - **No unspent points.** Nothing is spendable at zero, whatever the props
 *     say per skill. A spend with an empty pool is the client asking for
 *     something the server will refuse.
 *   - **Already at maximum.** A cell that renders `5/5` and still invites a
 *     click is incoherent, and the spend behind it would be refused too.
 *
 * Neither reads game state; both are bounds on numbers the panel is already
 * displaying. The caller is still expected to send `allocatable: false` for
 * both — these are the panel refusing to render a button whose only outcome is
 * a refusal, not a second opinion about the rules.
 */

/** One skill, as the caller has already resolved it. */
export interface TreeSkillView {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  /** Row in the tree, 1-based. D2 gates a tier behind character level. */
  readonly tier: number;
  /** Column within the tier, 1-based. */
  readonly column: number;
  /** Points already spent here. */
  readonly points: number;
  readonly maximumPoints: number;
  readonly requiredLevel: number;
  /** Skill ids that must be invested in first. */
  readonly prerequisites: readonly string[];
  /** The caller has already resolved every gate. The panel does not re-decide this. */
  readonly allocatable: boolean;
  /** Why not, when `allocatable` is false. Null when it is true. */
  readonly lockedReason: string | null;
}

export interface SkillTreeView {
  readonly id: string;
  readonly name: string;
  readonly skills: readonly TreeSkillView[];
}

export interface SkillTreePanelProps {
  readonly trees: readonly SkillTreeView[];
  readonly unspentSkillPoints: number;
  readonly onAllocate: (skillId: string) => void;
  readonly onClose: () => void;
}

/** Said when the pool is empty. */
export const NO_POINTS_REASON = 'No unspent skill points.';

/** Said when the skill cannot hold another point. */
export const AT_MAXIMUM_REASON = 'Already at maximum.';

/** Said when the caller locked a skill but supplied no reason. */
export const DEFAULT_LOCKED_REASON = 'Not available yet.';

/** A skill placed on the panel's grid, with the panel's decision about clicking it. */
export interface PlacedSkill {
  readonly skill: TreeSkillView;
  /** 1-based grid row. Mirrors `skill.tier`; named for the grid it drives. */
  readonly tier: number;
  /** 1-based grid column. */
  readonly column: number;
  /** Names of the prerequisites found in this tree, in the order given. */
  readonly prerequisiteNames: readonly string[];
  readonly spendable: boolean;
  /** Why not, when `spendable` is false. Null when it is true. */
  readonly blockedReason: string | null;
}

/** A line to draw between two cells of the grid. */
export interface SkillLink {
  readonly fromSkillId: string;
  readonly toSkillId: string;
  readonly fromTier: number;
  readonly fromColumn: number;
  readonly toTier: number;
  readonly toColumn: number;
}

export interface SkillTreeLayout {
  readonly treeId: string;
  readonly treeName: string;
  /** Points spent in this tree — what a D2 player actually navigates by. */
  readonly spentPoints: number;
  /** Highest tier present, so the grid is sized by the data. */
  readonly tierCount: number;
  /** Highest column present. */
  readonly columnCount: number;
  /** Every skill, ordered by tier, then column, then id. */
  readonly placed: readonly PlacedSkill[];
  readonly links: readonly SkillLink[];
}

export type ActivationOutcome =
  | { readonly kind: 'allocate'; readonly skillId: string }
  | { readonly kind: 'blocked'; readonly reason: string };

/**
 * Why a click on this skill would not spend a point, or `null` if it would.
 *
 * The caller's own reason comes first: *"requires level 30"* tells a player
 * more than *"no unspent skill points"*, even when both are true.
 */
const blockedReasonFor = (skill: TreeSkillView, unspentSkillPoints: number): string | null => {
  if (!skill.allocatable) return skill.lockedReason ?? DEFAULT_LOCKED_REASON;
  if (skill.points >= skill.maximumPoints) return AT_MAXIMUM_REASON;
  if (unspentSkillPoints <= 0) return NO_POINTS_REASON;
  return null;
};

/**
 * A total order over the cells: tier, then column, then id.
 *
 * The id tiebreak is what makes it *total*. Without it two skills sharing a
 * cell would keep the order they arrived in, and the panel would render
 * differently depending on how the caller happened to build its array — a
 * difference no test could pin down and every player would see as flicker.
 */
const byGridPosition = (left: TreeSkillView, right: TreeSkillView): number => {
  if (left.tier !== right.tier) return left.tier - right.tier;
  if (left.column !== right.column) return left.column - right.column;
  return left.id < right.id ? -1 : left.id > right.id ? 1 : 0;
};

/** Places one tree on the grid and resolves what may be clicked. */
export const layoutSkillTree = (tree: SkillTreeView, unspentSkillPoints: number): SkillTreeLayout => {
  const ordered = [...tree.skills].sort(byGridPosition);
  const byId = new Map(ordered.map((skill) => [skill.id, skill]));

  const placed: PlacedSkill[] = ordered.map((skill) => {
    // Resolved once, so `spendable` and `blockedReason` cannot disagree.
    const blockedReason = blockedReasonFor(skill, unspentSkillPoints);

    return {
      skill,
      tier: skill.tier,
      column: skill.column,
      prerequisiteNames: skill.prerequisites
        .map((id) => byId.get(id))
        .filter((prerequisite) => prerequisite !== undefined)
        .map((prerequisite) => prerequisite.name),
      spendable: blockedReason === null,
      blockedReason,
    };
  });

  const links: SkillLink[] = [];
  for (const skill of ordered) {
    for (const prerequisiteId of skill.prerequisites) {
      const prerequisite = byId.get(prerequisiteId);
      // A prerequisite in another tree has no cell here to draw from.
      if (prerequisite === undefined) continue;
      links.push({
        fromSkillId: prerequisite.id,
        toSkillId: skill.id,
        fromTier: prerequisite.tier,
        fromColumn: prerequisite.column,
        toTier: skill.tier,
        toColumn: skill.column,
      });
    }
  }

  return {
    treeId: tree.id,
    treeName: tree.name,
    spentPoints: ordered.reduce((total, skill) => total + skill.points, 0),
    tierCount: ordered.reduce((highest, skill) => Math.max(highest, skill.tier), 0),
    columnCount: ordered.reduce((highest, skill) => Math.max(highest, skill.column), 0),
    placed,
    links,
  };
};

/**
 * Every tree, in the order the caller gave them.
 *
 * All of them, not just the visible one: each tab shows its own spent total,
 * and a player picks a tab by that number.
 */
export const layoutSkillTrees = (
  trees: readonly SkillTreeView[],
  unspentSkillPoints: number,
): readonly SkillTreeLayout[] => trees.map((tree) => layoutSkillTree(tree, unspentSkillPoints));

/** What a click on this cell should do. */
export const activateSkill = (placed: PlacedSkill): ActivationOutcome =>
  placed.spendable
    ? { kind: 'allocate', skillId: placed.skill.id }
    : { kind: 'blocked', reason: placed.blockedReason ?? DEFAULT_LOCKED_REASON };

/**
 * The accessible name of a cell.
 *
 * Spent, maximum, required level and availability are all in the text. A
 * player using a screen reader gets what a sighted one reads off the border
 * colour and the number in the corner, which is the whole point: none of this
 * state is carried by colour alone.
 */
export const describeSkill = (placed: PlacedSkill): string => {
  const { skill } = placed;
  const parts = [
    `${skill.name}, ${skill.points} of ${skill.maximumPoints} points spent`,
    `requires character level ${skill.requiredLevel}`,
  ];
  if (placed.prerequisiteNames.length > 0) parts.push(`after ${placed.prerequisiteNames.join(' and ')}`);

  const head = parts.join(', ');
  return placed.blockedReason === null
    ? `${head}. Select to spend a point.`
    : `${head}. Unavailable: ${placed.blockedReason}`;
};

/**
 * The events the overlay swallows so the world beneath it never sees them.
 *
 * The panel floats over a live click-to-move canvas. A click that reaches the
 * canvas sends the character walking, so every one of these has to stop at the
 * panel's edge — including `onClick` itself, and `onContextMenu`, which in D2
 * is a second attack button.
 */
export const CONTAINED_POINTER_EVENTS = [
  'onPointerDown',
  'onPointerUp',
  'onPointerMove',
  'onClick',
  'onWheel',
  'onContextMenu',
] as const;

export type ContainedPointerEvent = (typeof CONTAINED_POINTER_EVENTS)[number];

/** The narrowest shape of an event this module needs. React's satisfy it. */
export interface StoppableEvent {
  readonly stopPropagation: () => void;
}

/** Handlers that swallow each contained event, ready to spread onto the overlay. */
export const containedPointerHandlers = (): Readonly<Record<ContainedPointerEvent, (event: StoppableEvent) => void>> =>
  Object.fromEntries(
    CONTAINED_POINTER_EVENTS.map((name) => [name, (event: StoppableEvent): void => { event.stopPropagation(); }]),
  ) as Readonly<Record<ContainedPointerEvent, (event: StoppableEvent) => void>>;
